import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Gear,
  Upload,
  Trash,
  PaintBucket,
  Image,
  Check,
  Circle,
} from '@phosphor-icons/react';
import { settingsAPI } from '@/services/api';
import { useBrandStore } from '@/store';

const Settings = () => {
  const navigate = useNavigate();
  const { logoUrl, companyName, primaryColor, secondaryColor, loginLogoSize, setBrandSettings } = useBrandStore();
  
  const [formData, setFormData] = useState({
    logo_url: logoUrl || '',
    company_name: companyName || '4Sat',
    primary_color: primaryColor || '#3b82f6',
    secondary_color: secondaryColor || '#10b981',
    login_logo_size: loginLogoSize || 'medium', // small, medium, large
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(logoUrl);

  // Load settings from server
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingsAPI.getBrand();
        const data = response.data;
        setFormData({
          logo_url: data.logo_url || '',
          company_name: data.company_name || '4Sat',
          primary_color: data.primary_color || '#3b82f6',
          secondary_color: data.secondary_color || '#10b981',
          login_logo_size: data.login_logo_size || 'medium',
        });
        setPreviewLogo(data.logo_url);
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    loadSettings();
  }, []);

  // Handle logo file upload (convert to base64 or URL)
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 2MB.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      setFormData(prev => ({ ...prev, logo_url: result }));
      setPreviewLogo(result);
    };
    reader.readAsDataURL(file);
  };

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    setSaved(false);
    
    try {
      await settingsAPI.updateBrand(formData);
      
      // Update store
      setBrandSettings({
        logoUrl: formData.logo_url,
        companyName: formData.company_name,
        primaryColor: formData.primary_color,
        secondaryColor: formData.secondary_color,
        loginLogoSize: formData.login_logo_size,
      });
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  // Clear logo
  const clearLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
    setPreviewLogo(null);
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="settings-page">
      {/* Header */}
      <header className="h-16 border-b border-gray-200 flex items-center justify-between px-4 bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-bold flex items-center gap-2">
              <Gear size={20} className="text-primary" />
              Configuración
            </h1>
            <p className="text-xs text-gray-500">
              Personaliza tu plataforma
            </p>
          </div>
        </div>
        
        <button
          onClick={saveSettings}
          disabled={saving}
          data-testid="save-settings-btn"
          className={`h-9 px-4 rounded-lg font-medium transition-all flex items-center gap-2 ${
            saved 
              ? 'bg-secondary text-secondary-foreground' 
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          } disabled:opacity-50`}
        >
          {saving ? (
            <>
              <Circle size={18} className="spinner" />
              Guardando...
            </>
          ) : saved ? (
            <>
              <Check size={18} />
              Guardado
            </>
          ) : (
            'Guardar Cambios'
          )}
        </button>
      </header>

      <main className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Brand Identity */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
            <Image size={20} /> Identidad de Marca
          </h2>
          
          <div className="space-y-6">
            {/* Company Name */}
            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">Nombre de la Empresa</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="4Sat"
                data-testid="company-name-input"
                className="w-full h-10 px-4 rounded-lg bg-white border border-gray-300 text-sm text-gray-800 focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-gray-500 mt-1">
                Este nombre aparecerá en el sidebar y la página de login
              </p>
            </div>

            {/* Logo Upload */}
            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">Logo</label>
              <div className="flex gap-4">
                {/* Preview */}
                <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50 overflow-hidden">
                  {previewLogo ? (
                    <img 
                      src={previewLogo} 
                      alt="Logo preview" 
                      className="w-full h-full object-contain p-2"
                      data-testid="logo-preview"
                    />
                  ) : (
                    <div className="text-center text-gray-500">
                      <Image size={32} className="mx-auto mb-1" />
                      <span className="text-xs">Sin logo</span>
                    </div>
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1 space-y-3">
                  <div>
                    <label 
                      htmlFor="logo-upload"
                      className="inline-flex h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors items-center gap-2 cursor-pointer"
                    >
                      <Upload size={18} /> Subir Logo
                    </label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                  </div>
                  
                  {previewLogo && (
                    <button
                      onClick={clearLogo}
                      className="h-10 px-4 rounded-lg bg-destructive/10 text-destructive font-medium hover:bg-destructive/20 transition-colors flex items-center gap-2"
                      data-testid="clear-logo-btn"
                    >
                      <Trash size={18} /> Eliminar Logo
                    </button>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    Formatos: PNG, JPG, SVG. Máximo 2MB. Recomendado: 200x60px
                  </p>
                  
                  {/* Logo Size for Login */}
                  {previewLogo && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <label className="text-xs text-gray-600 mb-2 block font-medium">
                        Tamaño del logo en Login:
                      </label>
                      <div className="flex gap-2">
                        {[
                          { value: 'small', label: 'Pequeño', height: 'h-16' },
                          { value: 'medium', label: 'Mediano', height: 'h-24' },
                          { value: 'large', label: 'Grande', height: 'h-32' },
                        ].map((size) => (
                          <button
                            key={size.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, login_logo_size: size.value }))}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                              formData.login_logo_size === size.value
                                ? 'bg-primary text-white'
                                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {size.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* URL Input Alternative */}
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">O ingresa una URL:</label>
                    <input
                      type="url"
                      value={formData.logo_url.startsWith('data:') ? '' : formData.logo_url}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, logo_url: e.target.value }));
                        setPreviewLogo(e.target.value || null);
                      }}
                      placeholder="https://ejemplo.com/logo.png"
                      data-testid="logo-url-input"
                      className="w-full h-9 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Colors */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
            <PaintBucket size={20} /> Colores
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Primary Color */}
            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">Color Primario</label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                  data-testid="primary-color-input"
                  className="w-12 h-12 rounded-lg cursor-pointer border border-gray-200"
                />
                <input
                  type="text"
                  value={formData.primary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="flex-1 h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm font-mono text-gray-800"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Botones principales, enlaces y acentos
              </p>
            </div>

            {/* Secondary Color */}
            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">Color Secundario</label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={formData.secondary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value }))}
                  data-testid="secondary-color-input"
                  className="w-12 h-12 rounded-lg cursor-pointer border border-gray-200"
                />
                <input
                  type="text"
                  value={formData.secondary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value }))}
                  className="flex-1 h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm font-mono text-gray-800"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Estados activos, éxito y confirmaciones
              </p>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
            <p className="text-xs text-gray-500 mb-3">Vista previa:</p>
            <div className="flex gap-3">
              <button
                style={{ backgroundColor: formData.primary_color }}
                className="h-10 px-4 rounded-lg text-gray-800 font-medium"
              >
                Botón Primario
              </button>
              <button
                style={{ backgroundColor: formData.secondary_color }}
                className="h-10 px-4 rounded-lg text-gray-800 font-medium"
              >
                Botón Secundario
              </button>
              <div 
                style={{ borderColor: formData.primary_color }}
                className="h-10 px-4 rounded-lg border-2 flex items-center font-medium"
              >
                Outline
              </div>
            </div>
          </div>
        </section>

        {/* Info */}
        <section className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-sm text-gray-500">
            <strong>Nota:</strong> Los cambios en la configuración se guardan en tu cuenta y se aplicarán 
            en todos los dispositivos donde inicies sesión. El logo y los colores personalizados reemplazarán 
            los valores predeterminados de la plataforma.
          </p>
        </section>
      </main>
    </div>
  );
};

export default Settings;
